import type { CredentialResponse, GoogleCredentialResponse, IdConfiguration, MomentListener } from '../types/types';
import { useEffect, useRef } from 'react';
import { useLoadGoogleScript } from './useLoadGoogleScript';

interface UseGoogleOneTapLoginOptions {
  onSuccess: (credentialResponse: CredentialResponse) => void;
  onError?: () => void;
  promptMomentNotification?: MomentListener;
  cancel_on_tap_outside?: boolean;
  prompt_parent_id?: string;
  state_cookie_domain?: string;
  hosted_domain?: string;
  disabled?: boolean;
  use_fedcm_for_prompt?: IdConfiguration['use_fedcm_for_prompt'];
  auto_select?: boolean;
  client_id?: string;
}

/**
 * 谷歌 one tap 登录 hook 方法，基于开源库修改
 * @see https://github.com/MomenSherif/react-oauth/blob/master/packages/%40react-oauth/google/src/hooks/useGoogleOneTapLogin.ts
 */
export function useGoogleOneTapLogin({
  onSuccess,
  onError,
  promptMomentNotification,
  cancel_on_tap_outside,
  prompt_parent_id,
  state_cookie_domain,
  hosted_domain,
  use_fedcm_for_prompt = false,
  disabled,
  auto_select,
  client_id,
}: UseGoogleOneTapLoginOptions): void {
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const promptMomentNotificationRef = useRef(promptMomentNotification);
  promptMomentNotificationRef.current = promptMomentNotification;

  const { loaded } = useLoadGoogleScript();
  useEffect(() => {
    if (!client_id) {
      console.error('[useGoogleOneTapLogin] client_id is required but not provided');
      return;
    }

    if (!loaded) {
      return;
    }

    if (disabled) {
      window?.google?.accounts?.id?.cancel();
      return;
    }

    window?.google?.accounts?.id?.initialize({
      client_id,
      callback: (credentialResponse: GoogleCredentialResponse) => {
        if (!credentialResponse?.credential) {
          return onErrorRef.current?.();
        }

        const { credential, select_by } = credentialResponse;
        onSuccessRef.current({
          credential,
          clientId: client_id,
          select_by,
        });
      },
      hosted_domain,
      cancel_on_tap_outside,
      prompt_parent_id,
      state_cookie_domain,
      use_fedcm_for_prompt,
      auto_select,
    });

    window?.google?.accounts?.id?.prompt(promptMomentNotificationRef.current);

    return () => {
      window?.google?.accounts?.id?.cancel();
    };
  }, [
    loaded,
    client_id,
    cancel_on_tap_outside,
    prompt_parent_id,
    state_cookie_domain,
    hosted_domain,
    use_fedcm_for_prompt,
    disabled,
    auto_select,
  ]);
}
